/**
 * localStorage cache utility with per-type versioning and TTL
 *
 * Key format: bytemonk:events:v1, bytemonk:gallery:v2, etc.
 *
 * Payload structure:
 * {
 *   data: T,
 *   expiresAt: number,
 *   version: number
 * }
 *
 * Version bump policy: Only increment when data shape changes (new/removed fields, type changes),
 * not when content updates. This prevents unnecessary cache invalidation.
 */

const CACHE_PREFIX = "bytemonk:";

export type CacheType =
  | "events"
  | "gallery"
  | "faqs"
  | "team"
  | "sponsors"
  | "leaderboards";

interface CachePayload<T> {
  data: T;
  expiresAt: number;
  version: number;
}

/**
 * Current version numbers for each cache type
 * Increment when schema changes, not content updates
 */
const CACHE_VERSIONS: Record<CacheType, number> = {
  events: 1,
  gallery: 1,
  faqs: 1,
  team: 1,
  sponsors: 1,
  leaderboards: 1,
};

/**
 * Build cache key with type and version
 */
function buildKey(type: CacheType): string {
  const version = CACHE_VERSIONS[type];
  return `${CACHE_PREFIX}${type}:v${version}`;
}

/**
 * Set cache with TTL
 * @param type - Cache type (events, gallery, etc.)
 * @param data - Data to cache
 * @param ttlMs - Time to live in milliseconds
 * @returns true if successful, false if localStorage unavailable or quota exceeded
 */
export function setCache<T>(type: CacheType, data: T, ttlMs: number): boolean {
  try {
    const key = buildKey(type);
    const payload: CachePayload<T> = {
      data,
      expiresAt: Date.now() + ttlMs,
      version: CACHE_VERSIONS[type],
    };

    localStorage.setItem(key, JSON.stringify(payload));
    return true;
  } catch (error) {
    // Quota exceeded or localStorage unavailable
    console.warn(`Failed to set cache for ${type}:`, error);
    return false;
  }
}

/**
 * Get cache if valid and not expired
 * @param type - Cache type
 * @returns Cached data or null if not found, expired, or version mismatch
 */
export function getCache<T>(type: CacheType): T | null {
  try {
    const key = buildKey(type);
    const raw = localStorage.getItem(key);

    if (!raw) {
      return null;
    }

    const payload: CachePayload<T> = JSON.parse(raw);

    // Check version match
    if (payload.version !== CACHE_VERSIONS[type]) {
      // Version mismatch - schema changed, invalidate
      localStorage.removeItem(key);
      return null;
    }

    // Check expiry
    if (Date.now() > payload.expiresAt) {
      // Expired - remove and return null
      localStorage.removeItem(key);
      return null;
    }

    return payload.data;
  } catch (error) {
    // Parse error or localStorage unavailable
    console.warn(`Failed to get cache for ${type}:`, error);
    return null;
  }
}

/**
 * Clear specific cache type
 */
export function clearCache(type: CacheType): void {
  try {
    const key = buildKey(type);
    localStorage.removeItem(key);
  } catch (error) {
    console.warn(`Failed to clear cache for ${type}:`, error);
  }
}

/**
 * Clear all expired cache entries across all types
 * Call this on app initialization to clean up stale data
 */
export function clearExpired(): void {
  try {
    const now = Date.now();

    // Iterate through all cache types
    for (const type of Object.keys(CACHE_VERSIONS) as CacheType[]) {
      const key = buildKey(type);
      const raw = localStorage.getItem(key);

      if (!raw) continue;

      try {
        const payload: CachePayload<unknown> = JSON.parse(raw);

        // Remove if expired or version mismatch
        if (
          now > payload.expiresAt ||
          payload.version !== CACHE_VERSIONS[type]
        ) {
          localStorage.removeItem(key);
        }
      } catch {
        // Invalid JSON - remove it
        localStorage.removeItem(key);
      }
    }
  } catch (error) {
    console.warn("Failed to clear expired cache:", error);
  }
}

/**
 * Clear all ByteMonk cache entries (for debugging/logout)
 */
export function clearAllCache(): void {
  try {
    const keysToRemove: string[] = [];

    // Find all keys with our prefix
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }

    // Remove them
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    console.warn("Failed to clear all cache:", error);
  }
}

/**
 * Get cache size estimate in bytes
 */
export function getCacheSize(): number {
  try {
    let size = 0;

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(CACHE_PREFIX)) {
        const value = localStorage.getItem(key);
        if (value) {
          // Rough estimate: key + value length * 2 (UTF-16)
          size += (key.length + value.length) * 2;
        }
      }
    }

    return size;
  } catch (error) {
    console.warn("Failed to calculate cache size:", error);
    return 0;
  }
}
